// utils/customerProvider.jsx
import { useState, useCallback, useRef } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { useAlert } from '../hooks/useAlert';
import { CustomerContext } from './customerContext';

const API_URL = process.env.REACT_APP_API_URL;

export function CustomerProvider({ children }) {
    const [customers, setCustomers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [customerSlug, setCustomerSlug] = useState({});
    const [statuses, setStatuses] = useState([]);
    const [listStatus, setListStatus] = useState([]);
    const [searchedCustomer, setSearchedCustomer] = useState([]);
    const [customerCursor, setCustomerCursor] = useState(null);
    const [customerHasMore, setCustomerHasMore] = useState(true);
    const customerCursorRef = useRef(null);
    const customerHasMoreRef = useRef(true);
    const searchTimeoutRef = useRef(null);
    const [customersTotal, setCustomersTotal] = useState(0);

    const { showAlert } = useAlert();
    const navigate = useNavigate();

    const fetchCustomers = useCallback(async (reset = true, cursor = null, search = '') => {
        try {
            setLoading(true);

            if (reset) {
            setCustomerCursor(null);
            setCustomerHasMore(true);
            customerCursorRef.current = null;
            customerHasMoreRef.current = true;
            }

            const currentCursor = reset ? null : cursor ?? customerCursorRef.current;
            if (!reset && !customerHasMoreRef.current) {
            return;
            }

            const res = await axios.get(`${API_URL}/customers/getMyCustomers`, {
            withCredentials: true,
            params: {
                limit: 10,
                cursor: currentCursor || undefined,
                search: search || undefined,
            },
            });

            const fetched = res.data.customers || [];
            const pagination = res.data.pagination || {};

            setCustomers(prev => {
            if (reset) return fetched;
            const existingIds = new Set(prev.map((customer) => customer._id));
            const uniqueFetched = fetched.filter((customer) => !existingIds.has(customer._id));
            return [...prev, ...uniqueFetched];
            });
            setCustomersTotal(pagination.totalCount);

            setCustomerCursor(pagination.nextCursor || null);
            customerCursorRef.current = pagination.nextCursor || null;
            setCustomerHasMore(Boolean(pagination.hasMore));
            customerHasMoreRef.current = Boolean(pagination.hasMore);

            // ... dein listStatus-Teil bleibt gleich
        } catch (err) {
            showAlert('Fehler beim Laden der Kunden', 'error');
        } finally {
            setLoading(false);
        }
        }, [showAlert]);

    const saveCustomer = useCallback(async (customerData) => {
        try {
            await axios.post(
                `${API_URL}/customers`,
                customerData,
                { withCredentials: true }
            );
            showAlert('Kunde erfolgreich hinzugefügt', 'success');
            navigate('/dashboard/customer');
        } catch (err) {
            showAlert('Fehler beim Speichern des Kunden', 'error');
        }
    }, [showAlert, navigate]);

    const deleteCustomer = useCallback(async (id, prename, name) => {
        const confirmDelete = window.confirm(
            `Möchten Sie den Kunden ${prename} ${name} wirklich löschen? Dann werden auch alle zu ihm gehörenden Termine und Notizen gelöscht.`
        );
        if (!confirmDelete) return;

        try {
            await axios.delete(`${API_URL}/customers/${id}`, {
                withCredentials: true
            });

            setCustomers(prev => prev.filter(customer => customer._id !== id));
            showAlert('Kunde erfolgreich gelöscht', 'success');
        } catch (err) {
            showAlert('Fehler beim Löschen des Kunden', 'error');
        }
    }, [showAlert]);

    const fetchCustomerSlug = useCallback(async (id) => {
        try {
            const customerRes = await axios.get(`${API_URL}/customers/customer/${id}`, { withCredentials: true });
            const customer = customerRes.data.customer;
            setCustomerSlug(customer);
            setStatuses(customerRes.data.statuses || []);
        } catch (error) {
            showAlert('Fehler beim Laden des Kunden', 'error');
        }
    }, [showAlert]);

    const searchCustomer = useCallback(async (searchTerm) => {
        if (searchTimeoutRef.current) {
            clearTimeout(searchTimeoutRef.current);
        }

        if (!searchTerm) {
            setSearchedCustomer([]);
            return;
        }

        searchTimeoutRef.current = setTimeout(async () => {
            try {
                const res = await fetch(`${API_URL}/customers/getMyCustomers?search=${encodeURIComponent(searchTerm)}`, {
                    credentials: 'include',
                });
                const data = await res.json();
                setSearchedCustomer(data.customers || []);
            } catch (err) {
                setSearchedCustomer([]);
            }
        }, 300);
    }, []);

    const editCustomer = useCallback(async (editData, id, slug) => {
        try {
            await axios.patch(
                `${API_URL}/customers/${id}`,
                editData,
                { withCredentials: true }
            );
            showAlert('Kunde erfolgreich bearbeitet', 'success');
            await fetchCustomerSlug(slug);
        } catch (error) {
            showAlert('Fehler beim Bearbeiten des Kunden', 'error');
        }
    }, [fetchCustomerSlug, showAlert]);

    const value = {
        fetchCustomers,
        customers,
        loading,
        saveCustomer,
        deleteCustomer,
        refetch: fetchCustomers,
        customerSlug,
        fetchCustomerSlug,
        statuses,
        listStatus,
        searchCustomer,
        searchedCustomer,
        editCustomer,
        customerHasMore,
        customerCursor,
        customersTotal
    };

    return (
        <CustomerContext.Provider value={value}>
            {children}
        </CustomerContext.Provider>
    );
}